import React, { useEffect, useRef } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";

const SectionLabel = ({ children }) => (
  <div
    style={{
      fontSize: 10.5,
      fontFamily: FONT_MONO,
      letterSpacing: 0.6,
      color: COLORS.goldBright,
      textTransform: "uppercase",
      marginBottom: 6,
    }}
  >
    {children}
  </div>
);

const rowStyle = (highlight) => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "9px 10px",
  borderRadius: 8,
  background: highlight ? COLORS.blackCard2 : "transparent",
  border: `1px solid ${highlight ? "transparent" : COLORS.lineFaint}`,
  marginBottom: 5,
  cursor: "pointer",
  font: "inherit",
});

/** "2h ago" — precise enough for a notification, and no dependency to carry. */
function ago(iso) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

function describe(n) {
  const who = n.actor?.display_name || "Someone";
  if (n.type === "new_follower") return `👤 ${who} started following you`;
  const group = n.data?.group_name;
  return group ? `💬 New messages in ${group}` : "💬 New messages in a group";
}

export default function NotificationPanel({
  social,
  socialAvailable,
  careReminders,
  unreadCount,
  onClose,
  onMarkRead,
  onMarkAllRead,
  onOpenItem,
  onOpenRoom,
}) {
  const panelRef = useRef(null);

  /* Escape closes it, because a panel that can only be dismissed by hitting a
     small × is a panel people learn to avoid opening. */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const nothing = careReminders.length === 0 && social.length === 0;

  return (
    /* Absolute, not fixed. The app is a 420px-wide column centred in the
       viewport, and everything inside it anchors to that column — a fixed
       overlay would spill across the whole browser window and sit outside the
       phone frame on a desktop, which is exactly what the Toast had to avoid
       too. */
    <div
      onClick={onClose}
      style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60 }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Notifications"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: "calc(56px + env(safe-area-inset-top))",
          right: 12,
          left: 12,
          background: COLORS.blackCard,
          border: `1px solid ${COLORS.gold}`,
          borderRadius: 14,
          padding: 14,
          maxHeight: "68%",
          overflowY: "auto",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
          outline: "none",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontFamily: FONT_DISPLAY, fontSize: 16, color: COLORS.cream }}>Notifications</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {unreadCount > 0 && social.some((n) => !n.read) && (
              <button
                onClick={onMarkAllRead}
                style={{ background: "none", border: "none", color: COLORS.goldBright, fontFamily: FONT_BODY, fontSize: 11.5, cursor: "pointer", padding: 4 }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close notifications"
              style={{ background: "none", border: "none", color: COLORS.creamFaint, fontSize: 18, cursor: "pointer", lineHeight: 1, padding: "2px 4px" }}
            >
              ×
            </button>
          </div>
        </div>

        {nothing && (
          <div style={{ fontSize: 12.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, textAlign: "center", padding: "22px 0" }}>
            Nothing new right now.
          </div>
        )}

        {careReminders.length > 0 && (
          <div style={{ marginBottom: social.length ? 14 : 0 }}>
            <SectionLabel>Preservation</SectionLabel>
            {careReminders.map((n) => (
              <button
                key={n.id}
                onClick={() => { onOpenItem(n.itemId); onClose(); }}
                style={rowStyle(true)}
              >
                <span style={{ fontSize: 12.5, color: COLORS.cream, fontFamily: FONT_BODY }}>
                  {n.kind === "clean" ? "🧼" : "🔄"} {n.label}
                </span>
              </button>
            ))}
          </div>
        )}

        {social.length > 0 && (
          <div>
            <SectionLabel>Baggage Claim</SectionLabel>
            {social.map((n) => (
              <button
                key={n.id}
                onClick={() => {
                  if (!n.read) onMarkRead(n.id);
                  onOpenRoom(n.type === "group_message" ? n.data?.room_id || null : null);
                  onClose();
                }}
                style={rowStyle(!n.read)}
              >
                <span style={{ fontSize: 12.5, color: n.read ? COLORS.creamFaint : COLORS.cream, fontFamily: FONT_BODY }}>
                  {describe(n)}
                </span>
                <span style={{ display: "block", fontSize: 10.5, fontFamily: FONT_MONO, color: COLORS.creamFaint, marginTop: 2 }}>
                  {ago(n.created_at)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Said once, quietly, and only when it is actually true: the bell works
            and the community half of it simply has no backend yet on this
            project. Silence here would read as "nobody has followed you". */}
        {!socialAvailable && (
          <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 12, lineHeight: 1.5 }}>
            Baggage Claim notifications aren't set up on this project yet — supabase/migrations/0006_notifications.sql hasn't been applied.
          </div>
        )}
      </div>
    </div>
  );
}
